/**
 * ShopeeShippingSheet — Shipping options → label generation → download/print.
 *
 * Single Sheet with 3 states (in-dialog transitions per user-story.md):
 *   1. "options" — choose shipping method, address/time, label format
 *   2. "generating" — progress indicator while shipping + label generation
 *   3. "ready" — download/print the label PDF
 *
 * Plan 189, Epic 02.
 */

import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Download,
  Printer,
  Check,
  Copy,
  Package,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  useShippingParameters,
  useShipAndGenerateLabel,
  useReprintLabel,
  type ShipLabelParams,
} from "../api/orderHooks";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ShopeeShippingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderName: string;
  shopeeOrderSn: string;
  /** "ship" = full flow (options → generate). "reprint" = label format → download only. */
  mode?: "ship" | "reprint";
  /**
   * Fired once after a successful ship (status → SHIPPED, tracking assigned).
   * Callers outside the order-detail world (e.g. the pack station) use this to
   * refresh their own lists — the ship mutation only invalidates the orders
   * detail/list keys. Not fired on reprint (no status change).
   */
  onShipped?: () => void;
}

type SheetStep = "options" | "generating" | "ready";

const LABEL_FORMATS = [
  { value: "THERMAL_AIR_WAYBILL", label: "Thermal", desc: "100×150mm" },
  { value: "NORMAL_AIR_WAYBILL", label: "A4 (PDF)", desc: "Standard" },
] as const;

const GENERATION_STEPS = [
  "Arranging shipment...",
  "Requesting label...",
  "Generating...",
  "Downloading...",
];

function getStoredLabelFormat(): string {
  try {
    return localStorage.getItem("shopee_label_format") || "THERMAL_AIR_WAYBILL";
  } catch {
    return "THERMAL_AIR_WAYBILL";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShopeeShippingSheet({
  open,
  onOpenChange,
  orderName,
  shopeeOrderSn,
  mode = "ship",
  onShipped,
}: ShopeeShippingSheetProps) {
  const isReprint = mode === "reprint";

  // State machine — reprint starts at "options" too but shows only label format
  const [step, setStep] = useState<SheetStep>("options");

  // Options state
  const [shippingMethod, setShippingMethod] = useState<"pickup" | "dropoff">("pickup");
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<string | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [labelFormat, setLabelFormat] = useState(getStoredLabelFormat);

  // Generation state
  const [generationStep, setGenerationStep] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // PDF blob
  const pdfBlobRef = useRef<Blob | null>(null);
  const blobUrlsRef = useRef<string[]>([]);
  const [copied, setCopied] = useState(false);

  // Hooks — only fetch shipping params in ship mode
  const shippingParams = useShippingParameters(open && !isReprint ? orderName : "");
  const shipMutation = useShipAndGenerateLabel(orderName);
  const reprintMutation = useReprintLabel(orderName);

  // Pre-select first address/time slot when data loads
  useEffect(() => {
    if (!shippingParams.data) return;
    const { pickup, dropoff, info_needed } = shippingParams.data;
    if (info_needed === "pickup" && pickup) {
      setShippingMethod("pickup");
      if (pickup.address_list?.length && selectedAddressId === null) {
        setSelectedAddressId(pickup.address_list[0].address_id);
      }
      if (pickup.time_slot_list?.length && selectedTimeSlotId === null) {
        setSelectedTimeSlotId(pickup.time_slot_list[0].pickup_time_id);
      }
    } else if (info_needed === "dropoff" && dropoff) {
      setShippingMethod("dropoff");
    }
  }, [shippingParams.data, selectedAddressId, selectedTimeSlotId]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Generation timeout (30s)
  useEffect(() => {
    if (step !== "generating") return;
    const timer = setTimeout(() => setTimedOut(true), 30_000);
    return () => clearTimeout(timer);
  }, [step]);

  // Simulate progress steps during generation
  useEffect(() => {
    if (step !== "generating") return;
    setGenerationStep(0);
    const interval = setInterval(() => {
      setGenerationStep((prev) => Math.min(prev + 1, GENERATION_STEPS.length - 1));
    }, 2000);
    return () => clearInterval(interval);
  }, [step]);

  // Handle sheet close: cleanup
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrlsRef.current = [];
      pdfBlobRef.current = null;
      setStep("options");
      setTimedOut(false);
      setCopied(false);
      shipMutation.reset();
      reprintMutation.reset();
    }
    onOpenChange(isOpen);
  };

  // Confirm & Generate
  const handleConfirm = () => {
    const methodParams: Record<string, any> = {};

    if (shippingMethod === "pickup") {
      if (!selectedAddressId || !selectedTimeSlotId) {
        toast.error("Please select a pickup address and time slot");
        return;
      }
      methodParams.address_id = selectedAddressId;
      methodParams.pickup_time_id = selectedTimeSlotId;
    } else {
      if (!selectedBranchId) {
        toast.error("Please select a dropoff branch");
        return;
      }
      methodParams.branch_id = selectedBranchId;
    }

    const params: ShipLabelParams = {
      shippingMethod,
      methodParams,
      labelFormat,
    };

    setStep("generating");
    shipMutation.mutate(params, {
      onSuccess: (blob) => {
        pdfBlobRef.current = blob;
        // Save label format preference
        try {
          localStorage.setItem("shopee_label_format", labelFormat);
        } catch {
          // localStorage unavailable
        }
        setStep("ready");
        onShipped?.();
      },
      onError: () => {
        // Error already handled via hook onError (toast)
        setStep("options");
      },
    });
  };

  // Reprint: skip ship_order, just regenerate label
  const handleReprint = () => {
    setStep("generating");
    reprintMutation.mutate(labelFormat, {
      onSuccess: (blob) => {
        pdfBlobRef.current = blob;
        try {
          localStorage.setItem("shopee_label_format", labelFormat);
        } catch {
          // localStorage unavailable
        }
        setStep("ready");
      },
      onError: () => {
        setStep("options");
      },
    });
  };

  // Download PDF
  const handleDownload = () => {
    if (!pdfBlobRef.current) return;
    const url = URL.createObjectURL(pdfBlobRef.current);
    blobUrlsRef.current.push(url);
    const link = document.createElement("a");
    link.href = url;
    link.download = `shopee-label-${shopeeOrderSn}.pdf`;
    link.click();
  };

  // Print PDF via hidden iframe
  const handlePrint = () => {
    if (!pdfBlobRef.current) return;
    const url = URL.createObjectURL(pdfBlobRef.current);
    blobUrlsRef.current.push(url);
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.print();
      } catch {
        // Fallback: open in new tab
        window.open(url, "_blank");
      }
      // Remove iframe after a delay to allow print dialog
      setTimeout(() => document.body.removeChild(iframe), 5000);
    };
  };

  // Copy tracking number
  const handleCopyTracking = (trackingNo: string) => {
    navigator.clipboard.writeText(trackingNo).then(() => {
      setCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Non-integrated check
  const isNonIntegrated = shippingParams.data?.info_needed === "non_integrated";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col overflow-y-auto">
        {step === "options" && isReprint && (
          <ReprintStep
            orderName={orderName}
            labelFormat={labelFormat}
            setLabelFormat={setLabelFormat}
            onConfirm={handleReprint}
          />
        )}

        {step === "options" && !isReprint && (
          <OptionsStep
            orderName={orderName}
            isLoading={shippingParams.isLoading}
            isError={shippingParams.isError}
            isNonIntegrated={isNonIntegrated}
            data={shippingParams.data}
            shippingMethod={shippingMethod}
            setShippingMethod={setShippingMethod}
            selectedAddressId={selectedAddressId}
            setSelectedAddressId={setSelectedAddressId}
            selectedTimeSlotId={selectedTimeSlotId}
            setSelectedTimeSlotId={setSelectedTimeSlotId}
            selectedBranchId={selectedBranchId}
            setSelectedBranchId={setSelectedBranchId}
            labelFormat={labelFormat}
            setLabelFormat={setLabelFormat}
            onConfirm={handleConfirm}
          />
        )}

        {step === "generating" && (
          <GeneratingStep
            currentStep={generationStep}
            timedOut={timedOut}
          />
        )}

        {step === "ready" && (
          <ReadyStep
            shopeeOrderSn={shopeeOrderSn}
            onDownload={handleDownload}
            onPrint={handlePrint}
            onCopyTracking={handleCopyTracking}
            copied={copied}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (inline — single file, no export)
// ---------------------------------------------------------------------------

function OptionsStep({
  orderName,
  isLoading,
  isError,
  isNonIntegrated,
  data,
  shippingMethod,
  setShippingMethod,
  selectedAddressId,
  setSelectedAddressId,
  selectedTimeSlotId,
  setSelectedTimeSlotId,
  selectedBranchId,
  setSelectedBranchId,
  labelFormat,
  setLabelFormat,
  onConfirm,
}: {
  orderName: string;
  isLoading: boolean;
  isError: boolean;
  isNonIntegrated: boolean;
  data: any;
  shippingMethod: "pickup" | "dropoff";
  setShippingMethod: (m: "pickup" | "dropoff") => void;
  selectedAddressId: number | null;
  setSelectedAddressId: (id: number) => void;
  selectedTimeSlotId: string | null;
  setSelectedTimeSlotId: (id: string) => void;
  selectedBranchId: number | null;
  setSelectedBranchId: (id: number) => void;
  labelFormat: string;
  setLabelFormat: (f: string) => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Ship Order
        </SheetTitle>
        <SheetDescription>{orderName}</SheetDescription>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="text-sm text-destructive text-center py-8">
            Failed to load shipping options. Please try again.
          </div>
        )}

        {isNonIntegrated && (
          <div className="rounded-lg border p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              This order uses a shipping method that must be arranged through
              Shopee Seller Center.
            </p>
            <a
              href="https://seller.shopee.co.th"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              Open Shopee Seller Center
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}

        {data && !isNonIntegrated && (
          <>
            {/* Shipping Method */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Shipping Method
              </label>
              <div className="space-y-2">
                {data.pickup && (
                  <MethodCard
                    selected={shippingMethod === "pickup"}
                    onSelect={() => setShippingMethod("pickup")}
                    title="Pickup"
                    description="Courier collects from your address"
                  />
                )}
                {data.dropoff && (
                  <MethodCard
                    selected={shippingMethod === "dropoff"}
                    onSelect={() => setShippingMethod("dropoff")}
                    title="Dropoff"
                    description="Drop at logistics branch"
                  />
                )}
              </div>
            </div>

            {/* Pickup options */}
            {shippingMethod === "pickup" && data.pickup && (
              <>
                {data.pickup.address_list?.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Pickup Address
                    </label>
                    <div className="space-y-2">
                      {data.pickup.address_list.map((addr: any) => (
                        <SelectableCard
                          key={addr.address_id}
                          selected={selectedAddressId === addr.address_id}
                          onSelect={() => setSelectedAddressId(addr.address_id)}
                          content={addr.address}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {data.pickup.time_slot_list?.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Pickup Time Slot
                    </label>
                    <div className="space-y-2">
                      {data.pickup.time_slot_list.map((slot: any) => (
                        <SelectableCard
                          key={slot.pickup_time_id}
                          selected={selectedTimeSlotId === slot.pickup_time_id}
                          onSelect={() => setSelectedTimeSlotId(slot.pickup_time_id)}
                          content={slot.time_text || new Date(slot.date * 1000).toLocaleDateString()}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Dropoff options */}
            {shippingMethod === "dropoff" && data.dropoff && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Logistics Branch
                </label>
                <div className="space-y-2">
                  {data.dropoff.branch_list?.map((branch: any) => (
                    <SelectableCard
                      key={branch.branch_id}
                      selected={selectedBranchId === branch.branch_id}
                      onSelect={() => setSelectedBranchId(branch.branch_id)}
                      content={`${branch.branch_name || ""} ${branch.address || ""}`.trim()}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Label Format */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Label Format
              </label>
              <div className="grid grid-cols-2 gap-2">
                {LABEL_FORMATS.map((fmt) => (
                  <button
                    key={fmt.value}
                    onClick={() => setLabelFormat(fmt.value)}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      labelFormat === fmt.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    <div className="text-sm font-medium">{fmt.label}</div>
                    <div className="text-xs text-muted-foreground">{fmt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {data && !isNonIntegrated && (
        <SheetFooter>
          <Button onClick={onConfirm} className="w-full">
            Confirm & Generate
          </Button>
        </SheetFooter>
      )}
    </>
  );
}

function ReprintStep({
  orderName,
  labelFormat,
  setLabelFormat,
  onConfirm,
}: {
  orderName: string;
  labelFormat: string;
  setLabelFormat: (f: string) => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Reprint Label
        </SheetTitle>
        <SheetDescription>{orderName}</SheetDescription>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        <p className="text-sm text-muted-foreground">
          This order has already been shipped. Choose the label format and download.
        </p>

        {/* Label Format */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Label Format
          </label>
          <div className="grid grid-cols-2 gap-2">
            {LABEL_FORMATS.map((fmt) => (
              <button
                key={fmt.value}
                onClick={() => setLabelFormat(fmt.value)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  labelFormat === fmt.value
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <div className="text-sm font-medium">{fmt.label}</div>
                <div className="text-xs text-muted-foreground">{fmt.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <SheetFooter>
        <Button onClick={onConfirm} className="w-full">
          <Download className="h-4 w-4 mr-2" />
          Generate Label
        </Button>
      </SheetFooter>
    </>
  );
}

function GeneratingStep({
  currentStep,
  timedOut,
}: {
  currentStep: number;
  timedOut: boolean;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle>Generating Label</SheetTitle>
      </SheetHeader>

      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
        <div className="relative">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>

        <div className="text-center space-y-1">
          <p className="text-sm font-medium">
            {GENERATION_STEPS[currentStep]}
          </p>
          <p className="text-xs text-muted-foreground">
            Step {currentStep + 1} of {GENERATION_STEPS.length}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5">
          {GENERATION_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-2 w-2 rounded-full transition-colors ${
                i <= currentStep ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>

        {timedOut && (
          <p className="text-xs text-muted-foreground text-center max-w-[240px]">
            This is taking longer than usual. You can close this dialog — the
            label will be available via "Reprint Label" when ready.
          </p>
        )}
      </div>
    </>
  );
}

function ReadyStep({
  shopeeOrderSn,
  onDownload,
  onPrint,
  onCopyTracking,
  copied,
}: {
  shopeeOrderSn: string;
  onDownload: () => void;
  onPrint: () => void;
  onCopyTracking: (trackingNo: string) => void;
  copied: boolean;
}) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Check className="h-5 w-5 text-green-500" />
          Label Ready
        </SheetTitle>
      </SheetHeader>

      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
        {/* Label preview placeholder */}
        <div className="w-32 h-44 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center">
          <Package className="h-10 w-10 text-muted-foreground/40" />
        </div>

        {/* Tracking number (uses the order SN as fallback display) */}
        <button
          onClick={() => onCopyTracking(shopeeOrderSn)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Order: {shopeeOrderSn}
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <SheetFooter className="flex-col gap-2 sm:flex-col">
        <div className="grid grid-cols-2 gap-2 w-full">
          <Button variant="outline" onClick={onDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          <Button onClick={onPrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Label saved. You can reprint from the order detail page anytime.
        </p>
      </SheetFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------

function MethodCard({
  selected,
  onSelect,
  title,
  description,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border hover:border-muted-foreground/30"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
            selected ? "border-primary" : "border-muted-foreground/40"
          }`}
        >
          {selected && <div className="h-2 w-2 rounded-full bg-primary" />}
        </div>
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
      </div>
    </button>
  );
}

function SelectableCard({
  selected,
  onSelect,
  content,
}: {
  selected: boolean;
  onSelect: () => void;
  content: string;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-lg border p-3 text-left text-sm transition-colors ${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border hover:border-muted-foreground/30"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="line-clamp-2">{content}</span>
        {selected && <Check className="h-4 w-4 text-primary shrink-0 ml-2" />}
      </div>
    </button>
  );
}
